import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  FaSchool,
  FaGraduationCap,
  FaHeart,
  FaLightbulb,
  FaUsers,
  FaBookOpen,
  FaAward,
  FaStar,
  FaBullseye,
  FaEye,
  FaCheckCircle,
} from "react-icons/fa";
import "./About.css";
import bgImage from "./standingkids.jpg";

const About = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      offset: 100,
    });
  }, []);

  return (
    <main className="about-page">
      {/* ================= HERO SECTION ================= */}
      <section
        className="about-hero"
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        <div className="about-hero-overlay"></div>

        <div className="about-hero-content" data-aos="fade-up">
          <div className="hero-icon-wrapper">
            <FaSchool />
          </div>

          <span className="hero-small-title">
            WELCOME TO OUR FOUNDATION
          </span>

          <h1>
            Building <span>Bright Futures</span>
            <br />
            Through Quality Education
          </h1>

          <p>
            Empowering learners with knowledge, character, creativity,
            confidence, and the values they need to make a meaningful
            difference in the world.
          </p>

          <div className="hero-buttons">
            <a href="#our-story" className="hero-primary-btn">
              Discover Our Story
            </a>

            <a href="#mission" className="hero-secondary-btn">
              Our Mission
            </a>
          </div>
        </div>

        <div className="hero-scroll-indicator">
          <span></span>
        </div>
      </section>

      {/* ================= INTRODUCTION ================= */}
      <section className="about-introduction" id="our-story">
        <div className="about-intro-container">
          <div
            className="about-intro-image"
            data-aos="fade-right"
          >
            <div className="intro-image-wrapper">
              <img
                src={bgImage}
                alt="Excellent Kids"
              />

              <div className="image-badge">
                <FaGraduationCap />
                <div>
                  <strong>Education</strong>
                  <span>That Inspires</span>
                </div>
              </div>
            </div>
          </div>

          <div
            className="about-intro-content"
            data-aos="fade-left"
          >
            <span className="section-label">WHO WE ARE</span>

            <h2>
              About The {" "}
              <span>
                 Excellent Kids 
              </span>{" "}
              Foundation
            </h2>

            <div className="section-divider"></div>

            <p>
              The Excellent Kids Foundation is committed to
              providing quality education and creating an environment where
              every learner has the opportunity to discover, develop, and
              maximize their God-given potential.
            </p>

            <p>
              We believe education is more than the transfer of knowledge.
              It is the foundation upon which character, leadership,
              creativity, confidence, and lifelong learning are built.
            </p>

            <p>
              Through innovative learning experiences, dedicated educators,
              and a strong commitment to excellence, we strive to prepare
              students to become responsible, confident, and productive
              members of society.
            </p>

            <div className="intro-highlights">
              <div>
                <FaCheckCircle />
                <span>Quality Education</span>
              </div>

              <div>
                <FaCheckCircle />
                <span>Character Development</span>
              </div>

              <div>
                <FaCheckCircle />
                <span>Student Excellence</span>
              </div>

              <div>
                <FaCheckCircle />
                <span>Future Leadership</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= MISSION / VISION ================= */}
      <section className="mission-section" id="mission">
        <div className="section-heading" data-aos="fade-up">
          <span className="section-label">OUR PURPOSE</span>

          <h2>
            Driven by <span>Purpose</span>
          </h2>

          <p>
            Everything we do is centered around creating meaningful
            educational opportunities and preparing learners for a brighter
            future.
          </p>
        </div>

        <div className="mission-grid">
          <div
            className="purpose-card mission-card"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <div className="purpose-icon">
              <FaBullseye />
            </div>

            <span>OUR MISSION</span>

            <h3>Empowering Through Education</h3>

            <p>
              To provide inclusive, innovative, and quality education that
              develops academic excellence, strong character, creativity,
              critical thinking, and leadership skills in every learner.
            </p>
          </div>

          <div
            className="purpose-card vision-card"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            <div className="purpose-icon">
              <FaEye />
            </div>

            <span>OUR VISION</span>

            <h3>A Brighter Future for Every Learner</h3>

            <p>
              To become a leading educational foundation recognized for
              developing confident, compassionate, innovative, and
              responsible individuals who positively impact their communities
              and the world.
            </p>
          </div>
        </div>
      </section>

      {/* ================= CORE VALUES ================= */}
      <section className="values-section">
        <div className="section-heading" data-aos="fade-up">
          <span className="section-label">WHAT WE BELIEVE</span>

          <h2>
            Our Core <span>Values</span>
          </h2>

          <p>
            Our values guide the way we teach, learn, lead, and serve our
            community.
          </p>
        </div>

        <div className="values-grid">
          <div
            className="value-card"
            data-aos="zoom-in"
            data-aos-delay="100"
          >
            <div className="value-icon">
              <FaBookOpen />
            </div>

            <h3>Excellence</h3>

            <p>
              We pursue high standards in teaching, learning, leadership,
              and personal development.
            </p>
          </div>

          <div
            className="value-card"
            data-aos="zoom-in"
            data-aos-delay="200"
          >
            <div className="value-icon">
              <FaHeart />
            </div>

            <h3>Integrity</h3>

            <p>
              We encourage honesty, responsibility, respect, and strong moral
              character in every member of our community.
            </p>
          </div>

          <div
            className="value-card"
            data-aos="zoom-in"
            data-aos-delay="300"
          >
            <div className="value-icon">
              <FaLightbulb />
            </div>

            <h3>Innovation</h3>

            <p>
              We inspire curiosity, creativity, critical thinking, and
              innovative approaches to learning.
            </p>
          </div>

          <div
            className="value-card"
            data-aos="zoom-in"
            data-aos-delay="400"
          >
            <div className="value-icon">
              <FaUsers />
            </div>

            <h3>Community</h3>

            <p>
              We believe strong partnerships between students, families,
              educators, and communities create lasting impact.
            </p>
          </div>

          <div
            className="value-card"
            data-aos="zoom-in"
            data-aos-delay="500"
          >
            <div className="value-icon">
              <FaAward />
            </div>

            <h3>Leadership</h3>

            <p>
              We prepare learners to lead with confidence, compassion,
              wisdom, and responsibility.
            </p>
          </div>

          <div
            className="value-card"
            data-aos="zoom-in"
            data-aos-delay="600"
          >
            <div className="value-icon">
              <FaStar />
            </div>

            <h3>Potential</h3>

            <p>
              We recognize the uniqueness of every learner and strive to
              provide opportunities for every student to thrive.
            </p>
          </div>
        </div>
      </section>

      {/* ================= EDUCATIONAL APPROACH ================= */}
      <section className="approach-section">
        <div className="approach-container">
          <div
            className="approach-content"
            data-aos="fade-right"
          >
            <span className="section-label">OUR APPROACH</span>

            <h2>
              Learning Beyond the{" "}
              <span>Classroom</span>
            </h2>

            <div className="section-divider"></div>

            <p>
              We understand that every learner is different. Our approach
              therefore goes beyond textbooks and examinations to create
              meaningful experiences that encourage students to think,
              explore, collaborate, create, and lead.
            </p>

            <p>
              We aim to create an educational environment where students feel
              valued, supported, challenged, and inspired to become the best
              version of themselves.
            </p>

            <div className="approach-points">
              <div>
                <span className="point-number">01</span>
                <div>
                  <h4>Discover</h4>
                  <p>Encouraging curiosity and exploration.</p>
                </div>
              </div>

              <div>
                <span className="point-number">02</span>
                <div>
                  <h4>Develop</h4>
                  <p>Building knowledge, skills, and character.</p>
                </div>
              </div>

              <div>
                <span className="point-number">03</span>
                <div>
                  <h4>Lead</h4>
                  <p>Preparing students to positively influence others.</p>
                </div>
              </div>
            </div>
          </div>

          <div
            className="approach-visual"
            data-aos="fade-left"
          >
            <div className="visual-card">
              <FaGraduationCap />

              <h3>Education</h3>

              <p>
                Knowledge that transforms lives, strengthens communities,
                and creates opportunities.
              </p>

              <div className="visual-stars">
                <FaStar />
                <FaStar />
                <FaStar />
                <FaStar />
                <FaStar />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= STATS ================= */}
      <section className="stats-section">
        <div className="stats-overlay"></div>

        <div className="stats-container">
          <div data-aos="fade-up">
            <FaGraduationCap />
            <strong>Quality</strong>
            <span>Education</span>
          </div>

          <div data-aos="fade-up" data-aos-delay="100">
            <FaUsers />
            <strong>Student</strong>
            <span>Centered</span>
          </div>

          <div data-aos="fade-up" data-aos-delay="200">
            <FaAward />
            <strong>Academic</strong>
            <span>Excellence</span>
          </div>

          <div data-aos="fade-up" data-aos-delay="300">
            <FaHeart />
            <strong>Strong</strong>
            <span>Character</span>
          </div>
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="about-cta" data-aos="fade-up">
        <div className="cta-content">
          <span className="section-label">JOIN THE JOURNEY</span>

          <h2>
            Inspiring Minds.
            <br />
            <span>Building Futures.</span>
          </h2>

          <p>
            Together, we can create an educational experience that empowers
            every learner to dream bigger, learn boldly, and make a lasting
            impact.
          </p>

          <a href="#contact" className="cta-button">
            Get in Touch
            <span>→</span>
          </a>
        </div>
      </section>
    </main>
  );
};

export default About;

